/* global globalThis */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Trash2, Plus, FileText, AlertCircle, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";

// Custom Confirmation Dialog Component
const ConfirmationDialog = ({ deleteConfirm, onCancel, onConfirm }) => {
  if (!deleteConfirm.show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900">Delete Object</h3>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this uploaded object?
            {deleteConfirm.objectName && (
              <span className="font-medium text-gray-700">
                {" "}
                "{deleteConfirm.objectName}"
              </span>
            )}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            This action cannot be undone.
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            No, Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmationDialog.propTypes = {
  deleteConfirm: PropTypes.shape({
    show: PropTypes.bool.isRequired,
    objectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    objectName: PropTypes.string,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const UploadedObjectsTable = () => {
  const navigate = useNavigate();
  const { Upload } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}",
  );
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCorporation, setSelectedCorporation] = useState({
    label: "",
    value: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const tableHeadings = ["Object Name", "Created On", "Status", "Actions"];

  // Add state for confirmation dialog
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    objectId: null,
    objectName: "",
  });

  useEffect(() => {
    const storedCorporation = localStorage.getItem("selectedCorporation");
    if (storedCorporation) {
      const parsedCorporation = JSON.parse(storedCorporation);
      setSelectedCorporation(parsedCorporation);
    }
  }, []);

  // 3. Add listener for localStorage changes (add after the above useEffect)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "organization_config_id") {
        const newConfigId = e.newValue;

        if (newConfigId) {
          fetchObjects();
        } else {
          setObjects([]);
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail.key === "organization_config_id") {
        const newConfigId = e.detail.newValue;

        if (newConfigId) {
          fetchObjects();
        } else {
          setObjects([]);
        }
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener(
      "customStorageChange",
      handleCustomStorageChange,
    );

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange,
      );
    };
  }, []);

  // 4. Add useEffect to fetch objects when selectedCorporation changes (add after the above useEffect)
  useEffect(() => {
    const orgConfigId = localStorage.getItem("organization_config_id");
    if (orgConfigId) {
      fetchObjects();
    }
  }, []);

  // Fetch uploaded objects based on corporation ID
  const fetchObjects = async () => {
    // Get organization config ID from localStorage instead of corporation ID
    const orgConfigId = localStorage.getItem("organization_config_id");

    if (!orgConfigId) {
      setObjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Updated API endpoint with organizationConfigId
      const response = await axiosClient.get(
        `/api/Mapper/GetMapperFilesbyOrgConfig/${orgConfigId}`,
      );

      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        setObjects(data);
      } else {
        setObjects([]);
        toast.error("Failed to load uploaded objects");
      }
    } catch (err) {
      setError(
        "Failed to fetch uploaded objects: " + (err.message || "Unknown error"),
      );
      setObjects([]);
      toast.error(
        "Error loading uploaded objects: " + (err.message || "Unknown error"),
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchObjects();
  }, []);

  // Handle delete with confirmation
  const handleDelete = async (objectId, objectName = "") => {
    // Show confirmation dialog
    setDeleteConfirm({
      show: true,
      objectId,
      objectName,
    });
  };

  const confirmDelete = async () => {
    const { objectId } = deleteConfirm;

    try {
      // Adjust the API endpoint and payload as needed for your delete endpoint
      const response = await axiosClient.delete(
        `/api/Mapper/DeleteUserMappingFile/${objectId}`,
      );

      if (response && (response.status === 200 || response.status === 204)) {
        setObjects((prev) =>
          prev.filter((obj) => obj.userMappingFileId !== objectId),
        );
        toast.success("Object deleted successfully");
      } else {
        toast.error("Failed to delete object");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(
          "Unable to delete object because it is being used in active mappings.",
        );
      } else {
        toast.error("Something went wrong while deleting the object");
      }
    } finally {
      setDeleteConfirm({ show: false, objectId: null, objectName: "" });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, objectId: null, objectName: "" });
  };

  const getFilteredObjects = () => {
    const orgConfigId = localStorage.getItem("organization_config_id");
    if (!orgConfigId) return [];

    let filtered = objects.filter(
      (obj) => obj.organizationConfigId === orgConfigId,
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((obj) => {
        const name = (obj.uploadedObjDescription || "").toLowerCase();
        const file = (obj.uploadedFileName || "").toLowerCase();
        return name.includes(query) || file.includes(query);
      });
    }
    return filtered;
  };

  const handleAddObject = () => {
    navigate("/upload/add-upload");
  };

  const getStatusBadge = () => {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-green-400" /> Active
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Objects
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchObjects}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - show only Add Object button
  const hasObjects = objects.some(
    (obj) => obj.organizationId === selectedCorporation.value,
  );
  if (!hasObjects && !loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Uploaded Objects
          </h3>
          {Upload?.canAdd && (
            <>
              <p className="text-gray-600 mb-6">
                Get started by adding your first object for{" "}
                <b>{selectedCorporation.label}.</b>
              </p>
              <button
                onClick={handleAddObject}
                className="inline-flex items-center text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload Mapping File
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Table view with data
  const hasObjectsForCorporation = objects.some(
    (obj) => obj.organizationId === selectedCorporation.value,
  );
  return (
    <>
      {hasObjectsForCorporation ? (
        <div className="p-6">
          {/* All elements in single line */}
          <div className="flex justify-between items-center mb-6 gap-6">
            {/* Heading Section */}
            <div className="flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">
                Uploaded Objects
              </h2>
              <p className="text-gray-600 text-sm">
                Manage uploaded objects for{" "}
                <span className="font-medium text-gray-900">
                  {selectedCorporation?.label || "Selected Corporation"}
                </span>
              </p>
            </div>

            {/* Right side: Search Bar and Upload Button */}
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="relative w-80">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full h-[42px] pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search by object name or file name..."
                />
              </div>

              {/* Upload Button */}
              {Upload?.canAdd && (
                <button
                  onClick={handleAddObject}
                  className="inline-flex items-center h-[42px] text-white px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-colors whitespace-nowrap flex-shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Upload Mapping File
                </button>
              )}
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {tableHeadings.map((heading) => {
                      const isCenterAligned = ["Status", "Actions"].includes(
                        heading,
                      );
                      if (!Upload.canDelete && heading === "Actions") {
                        return null;
                      }
                      return (
                        <th
                          key={heading}
                          className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            isCenterAligned ? "text-center" : "text-left"
                          }`}
                        >
                          {heading}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {loading ? (
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td colSpan="100%">
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-3 text-gray-600">
                            Loading uploaded objects...
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  (() => {
                    const filteredObjects = getFilteredObjects();
                    const hasFilteredResults = filteredObjects.length > 0;
                    if (!hasFilteredResults) {
                      return (
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td colSpan="100%">
                              <div className="flex flex-col items-center justify-center h-64">
                                <Search className="h-12 w-12 text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                  No results found
                                </h3>
                                <p className="text-gray-600">
                                  Try adjusting your search query
                                </p>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      );
                    }
                    return (
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getFilteredObjects().map((object) => (
                          <tr
                            key={object.userMappingFileId}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <FileText className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {object.uploadedObjDescription}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {object.uploadedFileName}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {object.created_at
                                ? new Date(object.created_at).toLocaleString()
                                : "—"}
                            </td>

                            <td className="px-6 py-4 text-center align-middle">
                              {getStatusBadge()}
                            </td>
                            {Upload.canDelete && (
                              <td className="px-6 py-4 text-center align-middle">
                                <button
                                  onClick={() =>
                                    handleDelete(
                                      object.userMappingFileId,
                                      object.uploadedObjDescription,
                                    )
                                  }
                                  className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100 transition-colors"
                                  title="Delete object"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    );
                  })()
                )}
              </table>
            </div>
          </div>

          <div className="my-4 text-sm text-gray-500">
            {(() => {
              const filteredCount = getFilteredObjects().length;
              const isSingular = filteredCount === 1;
              const pluralSuffix = isSingular ? "" : "s";
              return (
                <>
                  Showing {filteredCount} uploaded object{pluralSuffix} for{" "}
                  <span className="font-medium">
                    {selectedCorporation?.label || "Selected Corporation"}
                  </span>
                </>
              );
            })()}
          </div>

          {/* Custom Confirmation Dialog */}
          <ConfirmationDialog
            deleteConfirm={deleteConfirm}
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center loader-height">
          <div className="p-6">
            <div className="text-center py-12">
              <div className="mb-6 flex justify-center">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadedObjectsTable;
